import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import {
    fetchProjects,
    fetchServicesPage,
    fetchClients,
    fetchAwards,
    fetchSettings,
    mediaUrl,
} from "../lib/api";
import { HERO_IMG } from "../lib/branding";

const ABOUT_IMG =
    "https://static.prod-images.emergentagent.com/jobs/2981571f-77f9-42a9-85e2-d8389be9ce3e/images/07da495460665a99ca037554c1554cc623e47b122890182371b0098b09e2b384.png";

export default function Home() {
    const [projects, setProjects] = useState([]);
    const [services, setServices] = useState([]);
    const [servicesSubheading, setServicesSubheading] = useState(
        "Six disciplines, thoughtfully connected."
    );
    const [clients, setClients] = useState([]);
    const [awardsIntro, setAwardsIntro] = useState("");
    const [awardsIntroTitle, setAwardsIntroTitle] = useState("Awards & honours.");
    const [awardsArchiveLabel, setAwardsArchiveLabel] = useState("Archive 2011–2022 →");
    const [awardsArchivePath, setAwardsArchivePath] = useState("/awards/archive");
    const [awardGroups, setAwardGroups] = useState([]);
    const [activePeriod, setActivePeriod] = useState(null);
    const [heroImage, setHeroImage] = useState(HERO_IMG);
    const [studioSection, setStudioSection] = useState({
        eyebrow: "The Studio",
        heading_line_1: "Designing spaces that",
        heading_italic: "work beautifully.",
        heading_line_2: "",
        body: (
            "Founded in 2003, ASC Interior Co., Ltd. is a Bangkok-based studio " +
            "working across interior architecture, hospitality and bespoke " +
            "craftsmanship. Our approach is rooted in close collaboration—working " +
            "closely with clients, operators, developers and artisans—shaping " +
            "spaces that feel calm, intuitive and enduring. We focus as much on " +
            "the unseen details as the visible ones, balancing honest materials, " +
            "thoughtful operations and quietly efficient back-of-house design."
        ),
        button_label: "More about ASC",
        button_link: "/about",
        image: "",
        image_focal: null,
    });

    useEffect(() => {
        fetchProjects().then(setProjects).catch(() => {});
        fetchServicesPage()
            .then((p) => setServices(p?.items || []))
            .catch(() => {});
        fetchClients().then(setClients).catch(() => {});
        fetchSettings()
            .then((s) => {
                if (s?.hero_image) setHeroImage(mediaUrl(s.hero_image));
                if (s?.home_services_subheading) setServicesSubheading(s.home_services_subheading);
                if (s?.studio_section) setStudioSection((prev) => ({ ...prev, ...s.studio_section }));
            })
            .catch(() => {});
        fetchAwards()
            .then((data) => {
                setAwardsIntro(data?.intro || "");
                if (data?.intro_title) setAwardsIntroTitle(data.intro_title);
                if (data?.archive_link_label) setAwardsArchiveLabel(data.archive_link_label);
                if (data?.archive_link_path) setAwardsArchivePath(data.archive_link_path);
                const groups = data?.groups || [];
                setAwardGroups(groups);
                if (groups.length) setActivePeriod(groups[0].period);
            })
            .catch(() => {});
    }, []);

    const activeGroup = awardGroups.find((g) => g.period === activePeriod);

    return (
        <main data-testid="page-home">
            <Seo path="/" />
            {/* HERO */}
            <section
                data-testid="hero-section"
                className="relative min-h-[100svh] min-h-[100dvh] flex items-end overflow-hidden pt-24 md:pt-28"
            >
                <div className="absolute inset-0">
                    <img
                        src={heroImage}
                        alt="ASC Interior — hospitality lobby at golden hour"
                        className="w-full h-full object-cover object-center"
                        fetchPriority="high"
                        loading="eager"
                        decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-[#1A1A1A]/40 to-[#1A1A1A]/20" />
                </div>

                <div className="relative z-10 w-full px-6 md:px-12 lg:px-24 pb-14 md:pb-20 lg:pb-24">
                    <div className="max-w-5xl">
                        <Reveal>
                            <p className="asc-overline text-white" style={{ color: "#FFFFFF" }}>
                                Hospitality Interior Design — Founded 2003
                            </p>
                        </Reveal>
                        <Reveal delay={120}>
                            <h1
                                data-testid="hero-headline"
                                className="serif text-[#F9F8F6] mt-6 leading-[0.98] tracking-tight font-light"
                                style={{ fontSize: "clamp(2.5rem, 8vw, 7.5rem)" }}
                            >
                                Building Spaces.
                                <br />
                                Building <span className="italic text-[#E0CFB9]">Trust.</span>
                            </h1>
                        </Reveal>
                        <Reveal delay={260}>
                            <div className="mt-10 md:mt-12 max-w-2xl flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-8 text-[#F9F8F6]/90">
                                <p className="text-base sm:text-lg leading-relaxed font-light">
                                    We&apos;ve learned what matters: listening deeply,
                                    delivering consistently, designing for real
                                    people—not just photographs. Our practice has
                                    evolved. Our commitment hasn&apos;t. Hotels, resorts,
                                    restaurants, residences. Spaces where thoughtful
                                    design meets genuine hospitality.
                                </p>
                                <Link
                                    to="/projects"
                                    data-testid="hero-projects-cta"
                                    className="shrink-0 self-start md:self-auto inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] border-b border-[#F9F8F6]/50 pb-1 hover:border-[#F9F8F6]"
                                >
                                    See projects <ArrowUpRight size={14} />
                                </Link>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* INTRODUCTION */}
            <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 lg:py-40">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-4">
                        <Reveal>
                            <p className="asc-overline">A note from the studio</p>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-8">
                        <Reveal delay={80}>
                            <p
                                data-testid="intro-paragraph"
                                className="serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight font-light text-[#1A1A1A]"
                            >
                                We design hospitality interiors that age well—spaces
                                shaped by craft, restraint and a slow understanding of how
                                guests, operators and buildings actually behave.
                            </p>
                        </Reveal>
                        <Reveal delay={180}>
                            <div className="grid grid-cols-3 gap-8 mt-16 pt-10 border-t border-[#E5E1DB]">
                                {[
                                    { v: "22+", l: "Years of practice" },
                                    { v: "150+", l: "Hospitality projects" },
                                    { v: "35+", l: "International awards" },
                                ].map((s, i) => (
                                    <div
                                        key={i}
                                        data-testid={`stat-${i}`}
                                        className="flex flex-col"
                                    >
                                        <span className="serif text-4xl sm:text-5xl font-light text-[#1A1A1A]">
                                            {s.v}
                                        </span>
                                        <span className="asc-overline mt-3 text-[#8C7A6B]">
                                            {s.l}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ABOUT / IMAGE */}
            <section className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
                    <div className="lg:col-span-7 asc-img-zoom">
                        <img
                            src={mediaUrl(studioSection.image) || ABOUT_IMG}
                            alt={studioSection.eyebrow || "The Studio"}
                            className="w-full h-[60vh] lg:h-[80vh] object-cover"
                            style={studioSection.image_focal ? {
                                objectPosition: `${studioSection.image_focal.x ?? 50}% ${studioSection.image_focal.y ?? 50}%`,
                                transform: `scale(${studioSection.image_focal.zoom ?? 1})`,
                                transformOrigin: `${studioSection.image_focal.x ?? 50}% ${studioSection.image_focal.y ?? 50}%`,
                            } : undefined}
                        />
                    </div>
                    <div className="lg:col-span-5 lg:pl-8">
                        <Reveal>
                            <p className="asc-overline">{studioSection.eyebrow || "The Studio"}</p>
                            <h2 className="serif text-4xl lg:text-5xl mt-6 leading-[1.1] tracking-tight">
                                {studioSection.heading_line_1 || "Designing spaces that"}
                                {studioSection.heading_italic ? (
                                    <span className="italic"> {studioSection.heading_italic}</span>
                                ) : (
                                    <span className="italic"> work beautifully.</span>
                                )}
                                {studioSection.heading_line_2 && (
                                    <>
                                        <br />
                                        {studioSection.heading_line_2}
                                    </>
                                )}
                            </h2>
                            <p className="mt-8 text-base lg:text-lg leading-relaxed text-[#5C5855] font-light whitespace-pre-line">
                                {studioSection.body}
                            </p>
                            <Link
                                to={studioSection.button_link || "/about"}
                                data-testid="about-link"
                                className="inline-flex items-center gap-2 mt-10 text-xs uppercase tracking-[0.22em] border-b border-[#1A1A1A] pb-1"
                            >
                                {studioSection.button_label || "More about ASC"} <ArrowRight size={14} />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#F2EFEB]">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
                    <div className="lg:col-span-4">
                        <Reveal>
                            <p className="asc-overline">Services</p>
                            <h2 className="serif text-4xl lg:text-5xl mt-6 leading-[1.1] tracking-tight">
                                {servicesSubheading}
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6 self-end">
                        <Reveal delay={80}>
                            <p className="text-base lg:text-lg text-[#5C5855] leading-relaxed">
                                From concept to completion, our team works closely across
                                disciplines to shape projects with consistency, care and
                                attention to detail.
                            </p>
                        </Reveal>
                    </div>
                </div>

                <div data-testid="services-list" className="border-t border-[#D1CAC2]">
                    {services.map((svc, i) => (
                        <Reveal key={`${svc.number}-${i}`} delay={i * 60}>
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 py-8 lg:py-10 border-b border-[#D1CAC2] group">
                                <div className="lg:col-span-1 asc-overline text-[#8C7A6B]">
                                    {svc.number}
                                </div>
                                <div className="lg:col-span-4">
                                    <h3 className="serif text-2xl lg:text-3xl tracking-tight font-light">
                                        {svc.title}
                                    </h3>
                                </div>
                                <div className="lg:col-span-7 lg:col-start-6 text-[#5C5855] leading-relaxed text-base">
                                    {svc.description}
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* PORTFOLIO BENTO */}
            <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
                <div className="flex items-end justify-between mb-16">
                    <Reveal>
                        <p className="asc-overline">Selected Projects</p>
                        <h2 className="serif text-4xl lg:text-5xl mt-6 leading-[1.1] tracking-tight">
                            Recent <span className="italic">work.</span>
                        </h2>
                    </Reveal>
                    <Link
                        to="/projects"
                        data-testid="all-projects-link"
                        className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] asc-link"
                    >
                        All projects <ArrowUpRight size={14} />
                    </Link>
                </div>

                <div
                    data-testid="portfolio-grid"
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-start"
                >
                    {projects.slice(0, 6).map((p, i) => (
                        <Reveal key={p.slug} delay={(i % 3) * 80}>
                            <Link
                                to={`/projects/${p.slug}`}
                                data-testid={`portfolio-item-${p.slug}`}
                                className="block group h-full"
                            >
                                <div className="asc-img-zoom aspect-[4/5]">
                                    <img
                                        src={p.image}
                                        alt={p.title}
                                        className="w-full h-full object-cover"
                                        style={p.image_focal ? {
                                            objectPosition: `${p.image_focal.x ?? 50}% ${p.image_focal.y ?? 50}%`,
                                            transform: `scale(${p.image_focal.zoom ?? 1})`,
                                            transformOrigin: `${p.image_focal.x ?? 50}% ${p.image_focal.y ?? 50}%`,
                                        } : undefined}
                                    />
                                </div>
                                <div className="flex items-center justify-between mt-4">
                                    <div>
                                        <p className="asc-overline">{p.category} · {p.location}</p>
                                        <h3 className="serif text-2xl mt-2 tracking-tight">
                                            {p.title}
                                        </h3>
                                    </div>
                                    <span className="text-[#8C7A6B] text-sm">
                                        {p.year}
                                    </span>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* AWARDS */}
            <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#F2EFEB]">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-4">
                        <Reveal>
                            <p className="asc-overline">Recognition</p>
                            <h2
                                data-testid="awards-title"
                                className="serif text-4xl lg:text-5xl mt-6 leading-[1.1] tracking-tight"
                            >
                                {awardsIntroTitle}
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-8">
                        {awardsIntro && (
                            <Reveal>
                                <p
                                    data-testid="awards-intro"
                                    className="text-base lg:text-lg leading-relaxed text-[#5C5855] font-light mb-12"
                                >
                                    {awardsIntro}
                                </p>
                            </Reveal>
                        )}

                        {/* Year tabs */}
                        <div
                            data-testid="awards-tabs"
                            className="flex flex-wrap gap-2 mb-10 border-y border-[#D1CAC2] py-4"
                        >
                            {awardGroups.map((g) => (
                                <button
                                    key={g.period}
                                    onClick={() => setActivePeriod(g.period)}
                                    data-testid={`awards-tab-${g.period.replace(/\s|–/g, "")}`}
                                    className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] transition-colors ${
                                        activePeriod === g.period
                                            ? "bg-[var(--asc-yellow)] text-[#1A1A1A]"
                                            : "text-[#5C5855] hover:text-[#1A1A1A]"
                                    }`}
                                >
                                    {g.period}
                                </button>
                            ))}
                            <Link
                                to={awardsArchivePath}
                                data-testid="awards-archive-link"
                                className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A] ml-auto"
                            >
                                {awardsArchiveLabel}
                            </Link>
                        </div>

                        {/* Active group */}
                        <div data-testid="awards-list" className="space-y-2">
                            {activeGroup?.items.map((a, i) => {
                                const Inner = (
                                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 py-5 border-b border-[#E5E1DB] group">
                                        <span className="sm:col-span-8 serif text-lg lg:text-xl tracking-tight font-light group-hover:text-[var(--asc-accent-hover)] transition-colors">
                                            {a.title}
                                        </span>
                                        <span className="sm:col-span-3 sm:text-right text-sm text-[#5C5855] italic">
                                            {a.project}
                                        </span>
                                        <span className="sm:col-span-1 sm:text-right text-[var(--asc-yellow)] opacity-0 group-hover:opacity-100 transition-opacity">
                                            {a.project_slug ? "→" : ""}
                                        </span>
                                    </div>
                                );
                                return (
                                    <Reveal key={i} delay={i * 50}>
                                        {a.project_slug ? (
                                            <Link
                                                to={`/projects/${a.project_slug}`}
                                                data-testid={`award-link-${i}`}
                                                className="block"
                                            >
                                                {Inner}
                                            </Link>
                                        ) : (
                                            <div data-testid={`award-static-${i}`}>{Inner}</div>
                                        )}
                                    </Reveal>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* CLIENTS MARQUEE — final block before footer */}
            <section className="py-16 border-y border-[#E5E1DB] overflow-hidden bg-[#F9F8F6]">
                <div className="px-6 md:px-12 lg:px-24 mb-10">
                    <p className="asc-overline">Trusted by</p>
                </div>
                <div data-testid="clients-marquee" className="flex whitespace-nowrap">
                    <div className="asc-marquee-track flex shrink-0 items-center gap-[100px] pr-[100px]">
                        {[...clients, ...clients].map((c, i) => {
                            const item = typeof c === "string" ? { name: c, logo: null } : c;
                            const img = item.logo ? (
                                <img
                                    src={item.logo}
                                    alt={item.name}
                                    /* Monochrome default; hover restores original colour + weight
                                       via the `asc-logo` class (see App.css). mix-blend-multiply
                                       knocks out white backgrounds on transparent PNGs. */
                                    className="asc-logo h-[52px] md:h-[64px] w-auto object-contain shrink-0"
                                    style={{ mixBlendMode: "multiply" }}
                                    loading="lazy"
                                />
                            ) : (
                                <span className="serif text-2xl md:text-3xl text-[#8C7A6B] tracking-tight shrink-0">
                                    {item.name}
                                </span>
                            );
                            return item.website ? (
                                <a
                                    key={i}
                                    href={item.website}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label={item.name}
                                    className="shrink-0"
                                >
                                    {img}
                                </a>
                            ) : (
                                <div key={i} className="shrink-0">{img}</div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </main>
    );
}
